import React from 'react';
import { colors, fonts, sectionLabelStyle } from './styles';
import { Highlight } from './Highlight';

const callouts = [
  {
    label: 'Community',
    highlight: '2,500+ conditions',
    text: 'We steward an active community across ',
    tail: '. Participants stay because their experiences matter — and we steward them carefully.',
  },
  {
    label: 'Verification',
    highlight: 'Identity and diagnosis',
    text: '',
    tail: ' are confirmed through a multi-factor process. All participants provide informed consent.',
  },
  {
    label: 'Research',
    highlight: 'rigor',
    text: 'Every engagement is designed and delivered by our research team — with the ',
    tail: ' pharma teams require and the ',
    highlight2: 'care',
    tail2: ' patients deserve.',
  },
  {
    label: 'Patient Intelligence',
    highlight: 'years of listening',
    text: 'A decade capturing patient voice — not just methodology, but the instincts that come from ',
    tail: '.',
  },
];

const tags = ['Founded by patients', '10 years of community trust', 'Research + community in one'];

const founders = [
  {
    name: 'Jen Horonjeff, PhD',
    title: 'Co-Founder & CEO',
    bio: "A patient herself — living with autoimmune disease since childhood and a brain tumor as an adult — Jen brought her lived experience into a research career as a mixed-methods qualitative researcher, human factors engineer, and human-centered designer. Her doctorate focused on patient-centered outcomes, including what matters most to patients in clinical trial design. That work led to her role as an FDA advisor. She founded Real Patients to close the gap she'd spent her career studying.",
  },
  {
    name: 'Ronnie Sharpe',
    title: 'Co-Founder & Chief Product Officer',
    bio: 'A cystic fibrosis patient, Ronnie founded Cystic Life — one of the first rare disease social networks — capturing patient insights at scale before "patient engagement" was a priority for pharma. That work made him a sought-after consultant to pharmaceutical companies seeking to understand hard-to-reach patient populations. He brings that same understanding to everything Real Patients builds.',
  },
];

export const AboutSection = () => {
  return (
    <section
      style={{
        backgroundColor: colors.lightGray,
        padding: '96px 48px',
        boxSizing: 'border-box',
      }}
    >
      <style>{`
        @media (max-width: 768px) {
          .pi-about-wrap { padding-left: 24px !important; padding-right: 24px !important; padding-top: 64px !important; padding-bottom: 64px !important; }
          .pi-about-callouts { grid-template-columns: 1fr !important; }
          .pi-about-founders { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <div className="pi-about-wrap" style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto 48px', textAlign: 'center' }}>
          <p style={{ ...sectionLabelStyle, textAlign: 'center' }}>About Real Patients</p>
          <h2
            style={{
              fontFamily: fonts.serif,
              fontSize: 'clamp(26px, 3vw, 40px)',
              fontWeight: 700,
              color: colors.charcoal,
              margin: '0 0 20px',
              lineHeight: 1.2,
            }}
          >
            Founded by patients.
            <br />
            <span style={{ fontStyle: 'italic', fontWeight: 400 }}>
              Built by <Highlight>researchers</Highlight>.
            </span>
          </h2>
          <p
            style={{
              fontFamily: fonts.sans,
              fontSize: '16px',
              color: colors.warmGray,
              lineHeight: 1.75,
              fontWeight: 400,
              margin: '0 auto 8px',
              maxWidth: '620px',
              textWrap: 'pretty',
            }}
          >
            Most patient intelligence vendors are one thing: a panel, a platform, or a research firm.{' '}
            <strong style={{ color: colors.charcoal, fontWeight: 600 }}>Real Patients is all three</strong> — and that integration is what makes us fast, rigorous, and different.
          </p>
        </div>

        <div
          className="pi-about-callouts"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '14px',
            maxWidth: '960px',
            margin: '0 auto 28px',
          }}
        >
          {callouts.map((c) => (
            <div
              key={c.label}
              style={{
                backgroundColor: colors.white,
                border: `1px solid ${colors.border}`,
                borderRadius: '10px',
                padding: '18px 22px',
              }}
            >
              <div
                style={{
                  fontFamily: fonts.sans,
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '1.2px',
                  textTransform: 'uppercase',
                  color: colors.deepCoral,
                  marginBottom: '6px',
                }}
              >
                {c.label}
              </div>
              <div
                style={{
                  fontFamily: fonts.sans,
                  fontSize: '14px',
                  color: colors.warmGray,
                  lineHeight: 1.6,
                  fontWeight: 400,
                  textWrap: 'pretty',
                }}
              >
                {c.text}
                <strong style={{ color: colors.charcoal, fontWeight: 700 }}>{c.highlight}</strong>
                {c.tail}
                {c.highlight2 && <strong style={{ color: colors.charcoal, fontWeight: 700 }}>{c.highlight2}</strong>}
                {c.tail2}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            justifyContent: 'center',
            marginBottom: '56px',
          }}
        >
          {tags.map((t) => (
            <span
              key={t}
              style={{
                display: 'inline-block',
                backgroundColor: colors.coral,
                color: colors.white,
                fontFamily: fonts.sans,
                fontSize: '12px',
                fontWeight: 600,
                padding: '6px 14px',
                borderRadius: '20px',
                whiteSpace: 'nowrap',
              }}
            >
              {t}
            </span>
          ))}
        </div>

        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '40px',
            }}
          >
            <img
              src="/founders.png"
              alt="Jen Horonjeff and Ronnie Sharpe, co-founders of Real Patients"
              style={{
                width: '100%',
                maxWidth: '440px',
                height: 'auto',
                borderRadius: '16px',
                boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
              }}
            />
          </div>
          <div
            className="pi-about-founders"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '24px',
            }}
          >
            {founders.map((f) => (
              <div key={f.name}>
                <div
                  style={{
                    fontFamily: fonts.sans,
                    fontSize: '16px',
                    fontWeight: 600,
                    color: colors.charcoal,
                    marginBottom: '3px',
                  }}
                >
                  {f.name}
                </div>
                <div
                  style={{
                    fontFamily: fonts.sans,
                    fontSize: '10px',
                    color: colors.deepCoral,
                    fontWeight: 700,
                    letterSpacing: '1.2px',
                    textTransform: 'uppercase',
                    marginBottom: '12px',
                  }}
                >
                  {f.title}
                </div>
                <div
                  style={{
                    fontFamily: fonts.sans,
                    fontSize: '14px',
                    color: colors.warmGray,
                    lineHeight: 1.65,
                    fontWeight: 400,
                    textWrap: 'pretty',
                  }}
                >
                  {f.bio}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
